import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigation, useRevalidator, useRouteLoaderData, useSearchParams } from "react-router";
import { activateCheckoutIdentity, anonymousCheckoutTransfer, checkoutSteps, checkoutStorageKey, checkoutRecoveryDraft, emptyCheckoutDraft, readCheckoutDraft, restoreCheckoutURL, writeCheckoutDraft, type CheckoutDraft, type CheckoutStage } from "./checkout-state";

type CheckoutSessionValue = {
  draft: CheckoutDraft;
  scope: string;
  ready: boolean;
  error: string | null;
  update: (patch: Partial<CheckoutDraft>) => void;
  complete: (walletIntentID: string) => void;
  retainRecovery: (walletIntentID: string, keepAnswers?: boolean) => void;
  flush: () => Promise<boolean>;
  setStep: (step: CheckoutStage) => void;
  close: () => void;
  refreshUser: (step: CheckoutStage, signedIn?: boolean) => Promise<void>;
  canContinue: () => boolean;
};
const Context = createContext<CheckoutSessionValue | null>(null);
export function useCheckoutSession() {
  const value = useContext(Context);
  if (!value) throw new Error("CheckoutSessionProvider is required");
  return value;
}

export function CheckoutSessionProvider({ eventId, userId, children }: { eventId: string; userId?: string; children: ReactNode }) {
  const [search, setSearch] = useSearchParams();
  const setSearchRef = useRef(setSearch);
  setSearchRef.current = setSearch;
  const revalidator = useRevalidator();
  const revalidatorRef = useRef(revalidator);
  revalidatorRef.current = revalidator;
  const [draft, setDraft] = useState(emptyCheckoutDraft);
  const navigation = useNavigation();
    const rootData = useRouteLoaderData("root");
    const rootDataRef = useRef(rootData);
    rootDataRef.current = rootData;
    const identitySource = useRef<unknown>(null);
    const [refreshedUser, setRefreshedUser] = useState<{ scope: string; step: CheckoutStage; signedIn: boolean; rootData: unknown } | null>(null);
    const [identityLoaded, setIdentityLoaded] = useState(false);
  const [loadedScope, setLoadedScope] = useState("");
  const [error, setError] = useState<string | null>(null);
  const scope = checkoutStorageKey(eventId, userId);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const state = useRef({ draft, scope: "", unsaved: false });
  const transfer = useRef<{ draft: CheckoutDraft; sourceScope: string } | null>(null);
  const identityCheck = useRef<Promise<void> | null>(null);
    const finishIdentityCheck = useRef<(() => void) | null>(null);
  const identityVerified = useRef(true);
  const requestedStep = search.get("step");
  const open = requestedStep === "status" || checkoutSteps.some(step => step === requestedStep);
  const openRef = useRef(open);
  openRef.current = open;
  const searchRef = useRef(search);
  searchRef.current = search;
  const wasOpen = useRef(false);
  const ready = loadedScope === scope;

  const persist = useCallback((currentDraft: CheckoutDraft) => {
    try {
      writeCheckoutDraft(window.sessionStorage, scope, currentDraft);
      state.current.unsaved = false;
      setError(null);
    } catch {
      // Browser storage can be blocked or full. Keep the in-memory checkout usable.
      state.current.unsaved = true;
      setError("Browser storage is unavailable. Your progress is kept only while this page stays open.");
    }
  }, [scope]);

  const update = useCallback((patch: Partial<CheckoutDraft>) => {
    if (state.current.scope !== scope || scopeRef.current !== scope) return;
    const next = { ...state.current.draft, ...patch };
    if (JSON.stringify(next) === JSON.stringify(state.current.draft)) return;
    state.current.draft = next;
    persist(next);
    setDraft(next);
  }, [scope, persist]);

  const replaceAttemptDraft = useCallback((walletIntentID: string, replace: (draft: CheckoutDraft) => CheckoutDraft) => {
    if (!walletIntentID || scopeRef.current !== scope || state.current.scope !== scope || state.current.draft.walletIntentID !== walletIntentID) return;
    let current = state.current.draft;
    try {
      // A nested attendee form can have newer data in the same tab.
      const saved = readCheckoutDraft(window.sessionStorage, scope);
      if (saved && saved.walletIntentID !== walletIntentID) return;
      if (saved && !state.current.unsaved) current = saved;
    } catch { /* Storage failures must not prevent clearing the in-memory draft. */ }
    const next = replace(current);
    state.current.draft = next;
    persist(next);
    setDraft(next);
  }, [scope, persist]);
  const complete = useCallback((walletIntentID: string) => replaceAttemptDraft(walletIntentID, emptyCheckoutDraft), [replaceAttemptDraft]);
  const retainRecovery = useCallback((walletIntentID: string, keepAnswers = false) => replaceAttemptDraft(walletIntentID, current => ({
    ...checkoutRecoveryDraft(walletIntentID), ...(keepAnswers ? { answers: current.answers } : {}),
  })), [replaceAttemptDraft]);

  const flush = useCallback(async () => {
    await identityCheck.current;
    if (!identityVerified.current || state.current.scope !== scope || scopeRef.current !== scope) return false;
    persist(state.current.draft);
    return true;
  }, [scope, persist]);

  useEffect(() => {
    let saved: CheckoutDraft | null = null;
    let storageAvailable = true;
    try {
      activateCheckoutIdentity(window.sessionStorage, userId);
      saved = readCheckoutDraft(window.sessionStorage, scope);
    } catch {
      storageAvailable = false;
      setError("Browser storage is unavailable. Your progress is kept only while this page stays open.");
    }
    const signingIn = transfer.current?.sourceScope !== scope ? transfer.current : null;
    const unsaved = state.current.scope === scope && state.current.unsaved;
    const next = (unsaved ? state.current.draft : saved) ?? signingIn?.draft ?? emptyCheckoutDraft();
    if (signingIn) transfer.current = null;
    state.current = { draft: next, scope, unsaved };
    wasOpen.current = false;
    if (storageAvailable) {
      setError(null);
      if (signingIn || unsaved || saved) persist(next);
    }
    if (signingIn && openRef.current && searchRef.current.get("step") !== "status") {
      setSearchRef.current(restoreCheckoutURL(searchRef.current.toString(), next), { replace: true, preventScrollReset: true });
    }
    setDraft(next);
    setLoadedScope(scope);
  }, [scope, userId, persist]);

  useEffect(() => {
    if (!ready) return;
    // Status is hydrated from the API, not an editable or resumable checkout step.
    if (requestedStep === "status") { wasOpen.current = false; return; }
    if (open && !wasOpen.current) {
      wasOpen.current = true;
      // Post-payment questions can update the same tab's draft in a nested route.
      try {
        const saved = readCheckoutDraft(window.sessionStorage, scope);
        if (saved && !state.current.unsaved) {
          state.current.draft = saved;
          setDraft(saved);
        }
      } catch { /* Keep the current in-memory draft when browser storage is blocked. */ }
      const current = searchRef.current;
      if (current.get("step") === "tickets" && state.current.draft.step !== "tickets") {
        setSearch(restoreCheckoutURL(current.toString(), state.current.draft), { replace: true, preventScrollReset: true });
        return;
      }
      const next = new URLSearchParams(current);
      let changed = false;
      for (const key of ["ticketVariationID", "discountCode"] as const) {
        if (!next.has(key) && state.current.draft[key]) { next.set(key, state.current.draft[key]); changed = true; }
      }
      if (changed) { setSearch(next, { replace: true, preventScrollReset: true }); return; }
    }
    if (!open) { wasOpen.current = false; return; }
    update({ step: requestedStep as CheckoutDraft["step"],
      ticketVariationID: search.get("ticketVariationID") ?? "",
      discountCode: search.get("discountCode") ?? "" });
  }, [open, ready, requestedStep, search, setSearch, update, scope]);

  useEffect(() => {
    identityVerified.current = true;
    // Auth cookies are shared across tabs, while drafts stay private to this tab.
    // Revalidate the existing root loader on return, without sending any draft data.
    const checkIdentity = () => {
      if (document.visibilityState === "hidden" || identityCheck.current) return;
      identityVerified.current = false;
      identitySource.current = rootDataRef.current;
      identityCheck.current = new Promise(resolve => { finishIdentityCheck.current = resolve; });
      void revalidatorRef.current.revalidate().then(() => {
        setIdentityLoaded(true);
      }).catch(() => {
        setError("We couldn't check your session. Refresh this page before continuing.");
        finishIdentityCheck.current?.();
        identityCheck.current = null;
      });
    };
    document.addEventListener("visibilitychange", checkIdentity);
    window.addEventListener("focus", checkIdentity);
    return () => {
      document.removeEventListener("visibilitychange", checkIdentity);
      window.removeEventListener("focus", checkIdentity);
      identityVerified.current = false;
      finishIdentityCheck.current?.();
    };
  }, []);

  useEffect(() => {
      // Revalidation promises may resolve before React commits the new root user.
      // Release waiting submissions only after both revalidation and navigation settle.
      if (!identityLoaded || rootData === identitySource.current || !ready || revalidator.state !== "idle" || navigation.state !== "idle") return;
      identityVerified.current = true;
      finishIdentityCheck.current?.();
      finishIdentityCheck.current = null;
      identityCheck.current = null;
      setIdentityLoaded(false);
    }, [identityLoaded, rootData, ready, revalidator.state, navigation.state]);

    const setStep = useCallback((step: CheckoutStage) => {
    if (state.current.scope !== scope || scopeRef.current !== scope) return;
    if (step !== "status") update({ step });
    setSearch(previous => { const next = new URLSearchParams(previous); next.set("step", step); return next; }, { replace: true, preventScrollReset: true });
  }, [scope, setSearch, update]);

  const close = useCallback(() => {
    if (scopeRef.current !== scope) return;
    openRef.current = false;
    if (state.current.scope === scope) persist(state.current.draft);
    setSearch(previous => { const next = new URLSearchParams(previous); next.delete("step"); return next; }, { replace: true, preventScrollReset: true });
  }, [scope, persist, setSearch]);

  const refreshUser = async (step: CheckoutStage, signedIn = false) => {
    if (state.current.scope !== scope || scopeRef.current !== scope) return;
    if (signedIn) transfer.current = { draft: anonymousCheckoutTransfer({ ...state.current.draft, step: step === "status" ? "tickets" : step }), sourceScope: scope };
    else if (!await flush()) return;
    await revalidator.revalidate();
    setRefreshedUser({ scope, step, signedIn, rootData });
  };
  useEffect(() => {
    if (!refreshedUser || rootData === refreshedUser.rootData || !ready || revalidator.state !== "idle" || navigation.state !== "idle") return;
    if (refreshedUser.scope === scope && (!refreshedUser.signedIn || userId)) {
      transfer.current = null;
      if (refreshedUser.signedIn && refreshedUser.step !== "status") update({ step: refreshedUser.step });
      if (openRef.current) setStep(refreshedUser.step);
    }
    setRefreshedUser(null);
  }, [refreshedUser, rootData, ready, scope, userId, revalidator.state, navigation.state, setStep, update]);
  const canContinue = useCallback(() => openRef.current && identityVerified.current && state.current.scope === scope && scopeRef.current === scope, [scope]);

  return <Context.Provider value={{ draft, scope, ready, error, update, complete, retainRecovery, flush, setStep, close, refreshUser, canContinue }}>{children}</Context.Provider>;
}
